import { useState, type FormEvent, type ReactNode } from 'react';
import { ANALYSIS_MISSIONS, ANALYSIS_MISSION_PRESETS, SITE_CONSENT_TEXT, type AnalysisMissionPreset, type CreateAnalysisInput } from '@daevio/shared';

export type MissionChoice = 'browse' | AnalysisMissionPreset | 'custom';

/** What the person typed: the site and what the visitors should try. */
export interface AnalysisDraft {
  url: string;
  choice: MissionChoice;
  task: string;
}

export const EMPTY_DRAFT: AnalysisDraft = { url: '', choice: 'browse', task: '' };

const CHOICES: MissionChoice[] = ['browse', ...ANALYSIS_MISSION_PRESETS, 'custom'];

/** The draft travels through sign-up in the query string of `/app/analyze`. */
export function draftToQuery(d: AnalysisDraft): string {
  const params = new URLSearchParams({ url: d.url.trim() });
  if (d.choice !== 'browse') params.set('mission', d.choice);
  if (d.choice === 'custom' && d.task.trim()) params.set('task', d.task.trim());
  return params.toString();
}

export function draftFromQuery(params: URLSearchParams): AnalysisDraft {
  const mission = params.get('mission') as MissionChoice | null;
  const choice = mission && CHOICES.includes(mission) ? mission : 'browse';
  return { url: (params.get('url') ?? '').slice(0, 2048), choice, task: choice === 'custom' ? (params.get('task') ?? '').slice(0, 300) : '' };
}

export function draftToInput(d: AnalysisDraft): CreateAnalysisInput {
  const body: CreateAnalysisInput = { url: d.url.trim(), consent: true };
  if (d.choice === 'custom') body.missionTask = d.task.trim();
  else if (d.choice !== 'browse') body.missionPreset = d.choice;
  return body;
}

interface Props {
  initial?: AnalysisDraft;
  /** The confirmation that the person owns the site or may test it (asked right before starting). */
  withConsent: boolean;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  busyLabel: string;
  note?: ReactNode;
  autoFocus?: boolean;
  /** `card`: the landing's look, the button in a footer with the note, `extra` between the choices and the footer. */
  variant?: 'card';
  extra?: ReactNode;
  /** Shown inside the submit button, after the label (the landing's arrow). */
  submitIcon?: ReactNode;
  onSubmit: (draft: AnalysisDraft) => void;
}

/** The site to test and what the three visitors should try: on the home page and on the start page. */
export function AnalysisForm({
  initial = EMPTY_DRAFT,
  withConsent,
  busy,
  error,
  submitLabel,
  busyLabel,
  note,
  autoFocus,
  variant,
  extra,
  submitIcon,
  onSubmit,
}: Props) {
  const [url, setUrl] = useState(initial.url);
  const [choice, setChoice] = useState<MissionChoice>(initial.choice);
  const [task, setTask] = useState(initial.task);
  const [consent, setConsent] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({ url, choice, task });
  };

  const disabled = busy || !url.trim() || (withConsent && !consent) || (choice === 'custom' && task.trim().length < 3);

  const choices = (
    <fieldset className="mission-choice">
      <legend>What should they try?</legend>
      <div className="chips" role="radiogroup">
        {CHOICES.map((c) => (
          <label key={c} className={choice === c ? 'chip selected' : 'chip'}>
            <input type="radio" name="mission" value={c} checked={choice === c} onChange={() => setChoice(c)} />
            {c === 'browse' ? 'Just browse' : c === 'custom' ? 'Something else' : ANALYSIS_MISSIONS[c].label}
          </label>
        ))}
      </div>
      {choice === 'custom' && (
        <label className="field">
          <span>Their task</span>
          <input
            type="text"
            maxLength={300}
            placeholder="Book a table for two on Friday evening"
            value={task}
            onChange={(e) => setTask(e.target.value)}
            required
          />
        </label>
      )}
    </fieldset>
  );

  if (variant === 'card') {
    return (
      <form className="analysis-form card" onSubmit={submit} aria-label="Test your site">
        <div className="af-body">
          <div className="af-url">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none' }}>
              <circle cx="12" cy="12" r="9" fill="none" stroke="#5E6B7E" strokeWidth="1.6" />
              <path d="M3 12h18M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18" fill="none" stroke="#5E6B7E" strokeWidth="1.6" />
            </svg>
            <label className="sr-only" htmlFor="analysis-url">
              Your site address
            </label>
            <input
              id="analysis-url"
              type="text"
              inputMode="url"
              autoComplete="url"
              placeholder="yoursite.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              autoFocus={autoFocus}
            />
          </div>
          {choices}
          {extra}
          {withConsent && (
            <label className="check consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
              <span>{SITE_CONSENT_TEXT}</span>
            </label>
          )}
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="af-foot">
          {note && <p className="form-note">{note}</p>}
          {/* An empty address is caught by `required`: the button keeps its colour until there is a real reason to wait. */}
          <button className="lx-btn brand" type="submit" disabled={busy || (withConsent && !consent) || (choice === 'custom' && task.trim().length < 3)}>
            {busy ? busyLabel : submitLabel}
            {submitIcon}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="analysis-form" onSubmit={submit} aria-label="Test your site">
      <label className="field big" htmlFor="analysis-url">
        <span>Your site</span>
      </label>
      <div className="url-row">
        <input
          id="analysis-url"
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="yoursite.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          autoFocus={autoFocus}
        />
        <button
          className="button brand big"
          type="submit"
          disabled={disabled}
        >
          {busy ? busyLabel : submitLabel}
        </button>
      </div>

      {choices}

      {withConsent && (
        <label className="check consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
          <span>{SITE_CONSENT_TEXT}</span>
        </label>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {note && <p className="form-note">{note}</p>}
    </form>
  );
}
